import
{
    NotificationPlugin
} from "@ews/push-notifications-plugin";

import
{
    authContext,
    //defaultProvider,
    persistence
} from '@ews/react-auth-context';

import
{
    FC,
    PropsWithChildren,
    useCallback,
    useEffect,
    useState
} from 'react';

import
{
    ProviderInterface,
    useWebsocket
} from '../ReactData/ReactDataProvider';

import StateChangeListener = authContext.StateChangeListener;

import
{
    AuthRequest
} from '@ews/websocket-service';

import
{
    getDateFormat,
    User
} from '@ews/react-data';

import
{
    useUser,
    useUserDetails,
    useUserEventDisplay,
    useUserNotifySystems,
    useUserPermissions
} from '../ReactData/user';
import { copyTextOnKeyCombo } from "../Ux/keyboard";
import { ImpersonationState } from "../types";
import { usePersistentState } from "../Persistence";

type State = {
    isAuthenticated?: boolean,
    accessToken?: string;
};

type Persistence = {
    store(data: State): void;
    retrieve(): State;
};

let isAuthPending = false;

const websocketProvider = (
    channel: ProviderInterface,
    persistence?: Persistence
) =>
{

    let stateChange: StateChangeListener;
    let state: State = { ...(persistence ? persistence.retrieve() : {}), isAuthenticated: undefined };

    const isAuthenticated = () =>
    {
        return state.isAuthenticated;
    };

    const onStateChange = async (listener: StateChangeListener) =>
    {
        stateChange = listener;
    };

    const setState = (newState: State) =>
    {
        state = newState;
        if (persistence) persistence.store(state);
        if (stateChange) stateChange(state.isAuthenticated);

    };

    channel.removeAllListeners("auth:response");

    channel.on("auth:response", async (response: AuthRequest) =>
    {
        switch (response.type) {

            case 'auth':
                if (state.accessToken) {
                    //console.log("====== websocketProvider: auth:response: 'auth'");
                    NotificationPlugin.getToken().then(async (firebaseToken) =>
                    {
                        //console.log("====== websocketProvider: auth:response: 'auth': then: firebaseToken", firebaseToken);
                        if (firebaseToken.value) {
                            //console.log("====== websocketProvider: auth:response: 'auth': then: VALUE FOUND :)", firebaseToken.value);
                            await channel.auth({ type: "storeFirebaseToken", firebaseToken: firebaseToken.value });
                        }
                        else {
                            //console.log("====== websocketProvider: auth:response: 'auth': then: NO VALUE :(");
                        }
                    });

                    try {

                        if (!isAuthPending) {
                            isAuthPending = true;
                            await channel.auth({ type: "token", token: state.accessToken! }) as { type: 'token', token: string; };
                        }

                    } catch (e) {
                        setState({ isAuthenticated: false });
                    }
                } else {
                    setState({ isAuthenticated: false });
                }
                break;

            case 'token':
                //console.log("====== websocketProvider: auth:response: 'token'");

                // for debugging
                // console.info("THE TOKEN", response.token);
                // copy the token with Alt+Shift+T
                copyTextOnKeyCombo(response.token);

                isAuthPending = false;
                setState({ accessToken: response.token, isAuthenticated: true });
                NotificationPlugin.getToken().then(async (firebaseToken) =>
                {
                    //console.log("====== websocketProvider: auth:response: 'token': then: firebaseToken", firebaseToken);
                    if (firebaseToken.value) {
                        //console.log("====== websocketProvider: auth:response: 'token': then: VALUE FOUND :)", firebaseToken.value);
                        await channel.auth({ type: "storeFirebaseToken", firebaseToken: firebaseToken.value });
                    }
                    else {
                        //console.log("====== websocketProvider: auth:response: 'token': then: NO VALUE :(");
                    }
                });
                break;

            case 'logout':
                setState({ isAuthenticated: false });
                break;

        }
    });

    const login = async (username: string, password: string) =>
    {
        await channel.auth({ type: "login", username, password });
        return true;
    };

    const impersonate = async (userId: string) =>
    {
        await channel.auth({ type: "impersonate", userId, token: state.accessToken || "" });
        return true;
    };

    const refreshToken = async (_token: string) =>
    {
        await channel.auth({ type: "token", token: _token });
        return true;
    };


    const logout = async () =>
    {
        await channel.auth({ type: "logout", token: state.accessToken || "" });
        return true;
    };


    const getToken = () =>
    {
        return state.accessToken || "";
    };



    return { impersonate, refreshToken, login, logout, onStateChange, isAuthenticated, getToken };

};

const { default: WithAuth, AuthInvalid, AuthValid, AuthPending, useAuth } = authContext;

const waitingSettersMap: Map<ProviderInterface, React.Dispatch<React.SetStateAction<string>>[] | undefined> = new Map();
function addToAuthMeWaitingList(channel: ProviderInterface, setState: React.Dispatch<React.SetStateAction<string>>)
{
    const waitingSetters = waitingSettersMap.get(channel);

    if (!waitingSetters) {
        // start a new list of waiting setters
        const setters = [setState];
        waitingSettersMap.set(channel, setters);

        const handleResponse = (response: AuthRequest) =>
        {
            switch (response.type) {
                case 'me':
                    // unsubscribe
                    channel.off('auth:response', handleResponse);

                    // call all waiting setters
                    for (const setter of setters) {
                        setter(response.id || "");
                    }

                    // clear the waiting list so new subscriber will trigger a new auth
                    waitingSettersMap.set(channel, undefined);
                    break;
            }
        };

        // subscribe
        channel.on('auth:response', handleResponse);
        channel.auth({
            'type': 'me'
        });
        return;
    }

    // just add to the setter to the others already waiting for to be called.
    waitingSetters.push(setState);
}

function removeFromAuthMeWaitingList(channel: ProviderInterface, setState: React.Dispatch<React.SetStateAction<string>>)
{
    const waitingSetters = waitingSettersMap.get(channel);
    if (!waitingSetters) return;

    const idx = waitingSetters.indexOf(setState);
    if (idx === -1) return;
    waitingSetters.splice(idx, 1);
}

export const useMyProfileId = () =>
{
    const channel = useWebsocket();
    const [id, setId] = useState("");

    useEffect(() =>
    {
        addToAuthMeWaitingList(channel, setId);
        return (() =>
        {
            removeFromAuthMeWaitingList(channel, setId);
        });
    }, [channel]);

    return id;
};

// // old implementation that calls channel.on every time
// export const useMyProfileIdOld = () =>
// {
//     const channel = useWebsocket();
//     const [id, setId] = useState("");


//     useEffect(() =>
//     {
//         if (!id) {

//             console.log("useMyProfileId effect")
//             const handleResponse = (response: AuthRequest) =>
//             {
//                 console.log("useMyProfileId response")
//                 switch (response.type) {
//                     case 'me':
//                         channel.off('auth:response', handleResponse);
//                         setId(response.id || "");
//                         break;
//                 }
//             };

//             channel.on('auth:response', handleResponse);

//             channel.auth({
//                 'type': 'me'
//             });

//             return (() =>
//             {
//                 console.log("useMyProfileId effect cleanup")
//                 channel.off('auth:response', handleResponse);
//             });
//         }
//     }, [channel, id]);

//     return id;

// };


export const useMyProfileIdWithRefresh: () => [string, () => void] = () =>
{
    const channel = useWebsocket();
    const [id, setId] = useState("");
    const [refreshToken, setRefreshToken] = useState("");

    const refreshMyProfileId = useCallback(() =>
    {
        const newRefreshToken = (new Date()).toISOString();
        // console.log(`useMyProfileIdWithRefresh: refreshMyProfileId: ${newRefreshToken} vs ${refreshToken} changed? ${newRefreshToken !== refreshToken}`);
        setRefreshToken(newRefreshToken);
    }, [refreshToken]);

    useEffect(() =>
    {
        const handleResponse = (response: AuthRequest) =>
        {
            // console.log("useMyProfileId response", response.type, response);
            switch (response.type) {
                case 'me':
                    channel.off('auth:response', handleResponse);
                    setId(response.id || "");
                    break;
            }
        };

        channel.on('auth:response', handleResponse);

        channel.auth({
            'type': 'me'
        });

        return (() =>
        {
            // console.log("useMyProfileId effect cleanup");
            channel.off('auth:response', handleResponse);
        });
    }, [channel, refreshToken]);

    return ([id, refreshMyProfileId]);
};

export const useMyProfile = () =>
{
    const me = useMyProfileId();
    const profile = useUser(me);

    return profile;
};

export const useMyProfileWithRefresh: () => [User, () => void] = () =>
{
    const [me, refresh] = useMyProfileIdWithRefresh();
    const profile = useUser(me);

    return ([profile, refresh]);
};

export const useMyDetails = () =>
{
    const me = useMyProfileId();
    const details = useUserDetails(me);

    return details;
};

export const useMyDateFormat = () =>
{
    const { dateTimeFormat, timezone } = useMyDetails();
    return useCallback((date: Date = new Date(), timezoneOverride?: string, options?: Intl.DateTimeFormatOptions) => getDateFormat(dateTimeFormat!, date, timezoneOverride || timezone, options), [dateTimeFormat, timezone]);
};

export const useMyPermissions = () =>
{
    const me = useMyProfileId();
    const permissions = useUserPermissions(me);

    return permissions;
};

export const useMyEventDisplay = () =>
{
    const me = useMyProfileId();
    const eventDisplay = useUserEventDisplay(me);

    return eventDisplay;
};

export const useMyNotifySystems = () =>
{
    const me = useMyProfileId();
    return useUserNotifySystems(me);
};

export const useForgotPassword = () =>
{
    const channel = useWebsocket();

    return async (username: string) =>
    {
        channel.auth({ type: "forgotPassword", username });
        return true;
    };

};

// export const useSendDeveloperInformation = () =>
// {
//     const channel = useWebsocket();

//     return async (token: string) =>
//     {
//         //@ts-ignore
//         channel.auth({ type: "developerInformation", token });
//         return true;
//     };

// };

export const useResetPassword = () =>
{
    const channel = useWebsocket();

    return async (token: string, password: string) =>
    {
        channel.auth({ type: "resetPassword", token, password });
        return true;
    };

};

export const useForgotPasswordRpc = () =>
{
    const channel = useWebsocket();

    return async (username: string) =>
    {
        try {
            await channel.auth({ type: "forgotPasswordRpc", username });
            return true;
        } catch (e) {
            throw e;
        }
    };

};

export const useResetPasswordRpc = () =>
{
    const channel = useWebsocket();

    return async (token: string, password: string) =>
    {
        channel.auth({ type: "resetPasswordRpc", token, password });
        return true;
    };

};


const defaultImpersonationState = { impersonationStatus: "inactive" } satisfies ImpersonationState;

// usage: const [impersonationState, setImpersonationState, clearImpersonationState] = useImpersonationStateWithSetter();
export const useImpersonationStateWithSetter: () => [ImpersonationState, React.Dispatch<React.SetStateAction<ImpersonationState>>, () => void] = () =>
{
    const [impersonationState, setImpersonationState] = usePersistentState<ImpersonationState>('state: impersonationState', defaultImpersonationState, undefined, localStorage);
    const clearImpersonationState = useCallback(() =>
    {
        setImpersonationState(defaultImpersonationState);
    }, [setImpersonationState]);

    return [impersonationState, setImpersonationState, clearImpersonationState];
};

// usage: const impersonationState = useImpersonationState();
export const useImpersonationState = () => usePersistentState<ImpersonationState>('state: impersonationState', defaultImpersonationState, undefined, localStorage)[0];



const LoginProvider: FC<PropsWithChildren> = ({ children }) =>
{

    const channel = useWebsocket();
    const [provider, setProvider] = useState(() => websocketProvider(channel, new persistence.LocalStorage()));
    provider.onStateChange(() => setProvider({ ...provider }));

    return <WithAuth provider={provider}>{children}</WithAuth>;

};

export default LoginProvider;
export
{
    AuthInvalid, AuthPending, AuthValid, useAuth
};
